import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  DEFAULT_TERMINAL_APPROVAL_POLICY,
  TERMINAL_APPROVAL_RULES_MAX_COUNT,
  TerminalApprovalRuleSchema,
  ToolApprovalRuleSchema,
  type TerminalApprovalPolicy,
  type TerminalApprovalRule,
  type TerminalApprovalSettings,
  type ToolApprovalRule,
} from '@terra/shared-dto-chat-types';
import { createTenantStorage } from './tenantStorage';

type TerminalApprovalState = {
  policy: TerminalApprovalPolicy;
  rules: TerminalApprovalRule[];
  toolRules: ToolApprovalRule[];
  setPolicy: (policy: TerminalApprovalPolicy) => void;
  addRule: (rule: TerminalApprovalRule) => boolean;
  removeRule: (rule: TerminalApprovalRule) => void;
  addToolRule: (rule: ToolApprovalRule) => boolean;
  removeToolRule: (rule: ToolApprovalRule) => void;
  clearRules: () => void;
};

const STORAGE_KEY = 'terminal-approval-settings';

const isSameRule = (a: TerminalApprovalRule, b: TerminalApprovalRule) =>
  a.action === b.action && a.pattern === b.pattern;

const isSameToolRule = (a: ToolApprovalRule, b: ToolApprovalRule) =>
  a.action === b.action && a.tool === b.tool && a.argPattern === b.argPattern;

const appendUnique = <TRule>({
  rules,
  rule,
  isSame,
}: {
  rules: TRule[];
  rule: TRule;
  isSame: (a: TRule, b: TRule) => boolean;
}): TRule[] | undefined => {
  if (rules.some((existing) => isSame(existing, rule))) {
    return rules;
  }

  return rules.length >= TERMINAL_APPROVAL_RULES_MAX_COUNT
    ? undefined
    : [...rules, rule];
};

export const useTerminalApprovalStore = create<TerminalApprovalState>()(
  persist(
    (set, get) => ({
      policy: DEFAULT_TERMINAL_APPROVAL_POLICY,
      rules: [],
      toolRules: [],
      setPolicy: (policy) => set({ policy }),
      addRule: (candidate) => {
        const parsed = TerminalApprovalRuleSchema.safeParse(candidate);
        const rules = parsed.success
          ? appendUnique({
              rules: get().rules,
              rule: parsed.data,
              isSame: isSameRule,
            })
          : undefined;

        if (!rules) {
          return false;
        }

        set({ rules });

        return true;
      },
      removeRule: (rule) =>
        set((state) => ({
          rules: state.rules.filter((existing) => !isSameRule(existing, rule)),
        })),
      addToolRule: (candidate) => {
        const parsed = ToolApprovalRuleSchema.safeParse(candidate);
        const toolRules = parsed.success
          ? appendUnique({
              rules: get().toolRules,
              rule: parsed.data,
              isSame: isSameToolRule,
            })
          : undefined;

        if (!toolRules) {
          return false;
        }

        set({ toolRules });

        return true;
      },
      removeToolRule: (rule) =>
        set((state) => ({
          toolRules: state.toolRules.filter(
            (existing) => !isSameToolRule(existing, rule),
          ),
        })),
      clearRules: () => set({ rules: [], toolRules: [] }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => createTenantStorage()),
      partialize: (state) => ({
        policy: state.policy,
        rules: state.rules,
        toolRules: state.toolRules,
      }),
    },
  ),
);

export const getTerminalApprovalSettings = (): TerminalApprovalSettings => {
  const { policy, rules, toolRules } = useTerminalApprovalStore.getState();

  return toolRules.length > 0
    ? { policy, rules, toolRules }
    : { policy, rules };
};
